import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

type UseReleaseHeldReplacementOptions = {
  onSuccess?: (data?: unknown) => void
  onError?: (error?: unknown) => void
}

/**
 * VRG-275 — the brand-side escape hatch for a HELD replacement tag (one posted to
 * the owner rather than fitted on the spot, per `ReplaceTagModal`'s delivery
 * choice). Marks it received and activates it on the BRAND's word, without the
 * owner's own authenticated confirmation.
 *
 * This is deliberately NOT the normal path — the owner confirming receipt
 * themselves is — so it exists only to unstick a certificate when the owner can't
 * complete that flow (lost account access, unresponsive). The endpoint is
 * audit-logged as a brand action server-side so the two paths stay
 * distinguishable in the certificate's history.
 *
 * Mirrors the shape of `useCertificateArchiveMutations`.
 */
export const useReleaseHeldReplacement = (options?: UseReleaseHeldReplacementOptions) => {
  const queryClient = useQueryClient()

  const releaseMutation = useMutation({
    mutationFn: async (certificateId: string) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/admin/certificates/${certificateId}/replacement/release`,
      })
      return response.data
    },
    onSuccess: (data, certificateId) => {
      queryClient.invalidateQueries({ queryKey: ['certificates', 'detail', certificateId] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    releaseHeldReplacement: (certificateId: string) => releaseMutation.mutate(certificateId),
    isReleasingHold: releaseMutation.isPending,
  }
}
